import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { HeartPulse, MapPin, Phone, ArrowLeft, Info } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import AdmissionsTab from '../components/AdmissionsTab';
import { api } from '../api';

export default function HospiceDetailPage() {
  const { ccn } = useParams();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    setLoading(true);
    api.hospiceAgency(ccn).then((d) => {
      setA(d); setLoading(false);
      try {
        window.gtag && window.gtag('event', 'profile_view', {
          entity_type: 'hospice', ccn: d?.ccn, state: d?.state,
          profit_status: d?.profit_status || null,
        });
      } catch (_) {}
    }).catch(() => setLoading(false));
  }, [ccn]);

  if (loading) return <div className="min-h-screen"><Navbar /><main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-10"><div className="text-sm text-[#7A857F]">Loading agency…</div></main></div>;
  if (!a) return <div className="min-h-screen"><Navbar /><main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-10"><h1 className="font-display text-3xl">Agency not found</h1></main></div>;

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-10">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Hospice', to: '/hospice' }, { label: a.name }]} />

        <header className="mt-2 mb-6 flex items-start justify-between gap-6 flex-wrap">
          <div className="flex-1">
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10.5px] font-semibold uppercase tracking-wider bg-[#FBE6CC] text-[#A06628]">
              <HeartPulse size={11} /> Hospice Agency · CCN {a.ccn}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight text-[#1A2622] mt-2">{a.name}</h1>
            <div className="mt-2 flex flex-wrap gap-3 text-[12.5px] text-[#5A6660]">
              {a.address && <span className="flex items-center gap-1"><MapPin size={12} /> {a.address}, {a.city}, {a.state} {a.zip}</span>}
              {a.phone && <span className="flex items-center gap-1"><Phone size={12} /> {a.phone}</span>}
            </div>
          </div>
          <Link to="/hospice" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-[#E5DCC4] text-[#3A4A44] text-[12.5px] hover:bg-[#F5EFDD]">
            <ArrowLeft size={13} /> All hospices
          </Link>
        </header>

        <div className="mt-6 border-b border-[#E5DCC4] flex gap-1" data-testid="hospice-tabs">
          {[{id:'overview',label:'Overview'},{id:'admissions',label:'Admissions Activity'}].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`hospice-tab-${t.id}`}
              className={`px-4 h-10 text-[12.5px] font-medium border-b-2 -mb-px transition ${tab === t.id ? 'border-[#A06628] text-[#1A2622]' : 'border-transparent text-[#7A857F] hover:text-[#1A2622]'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <>
            <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
            <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">Ownership type</div>
            <div className="mt-1 text-[13px] text-[#1A2622]">{a.ownership || '—'}</div>
          </div>
          <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
            <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">Profit status</div>
            <div className={`mt-1 text-[13px] font-semibold ${a.profit_status === 'For Profit' ? 'text-[#A06628]' : 'text-[#2D5F4F]'}`}>{a.profit_status || '—'}</div>
          </div>
          <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
            <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">Certified</div>
            <div className="mt-1 text-[13px] text-[#1A2622]">{a.certification_date || '—'}</div>
          </div>
          <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
            <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">ZIP</div>
            <div className="mt-1 text-[13px] text-[#1A2622] font-mono">{a.zip || '—'}</div>
          </div>
        </div>

        <div className="mt-5 p-5 rounded-xl bg-[#1A2622] text-[#FBF6EC]">
          <div className="text-[11px] uppercase tracking-wider text-[#D4A85C] font-semibold mb-3">In the post-acute network</div>
          <p className="text-[13px] opacity-90 leading-relaxed">
            This hospice is geocoded alongside every CMS-certified SNF and acute-care hospital within 25 miles.
            Visit any nearby <Link to="/facilities" className="underline text-[#D4A85C]">SNF</Link> or <Link to="/hospitals" className="underline text-[#D4A85C]">hospital</Link> profile and open the <strong className="text-white">Post-Acute Network</strong> tab to see this agency listed as an end-of-life care partner.
          </p>
        </div>

        <div className="mt-5 flex items-start gap-2 text-[11.5px] text-[#A8B0AA] leading-relaxed">
          <Info size={12} className="mt-0.5 shrink-0" />
          <span><strong>Source:</strong> {a.source}. Public CMS data only — no PHI.</span>
        </div>
          </>
        )}

        {tab === 'admissions' && <AdmissionsTab kind="hospice" ccn={a.ccn} />}
      </main>
      <Footer />
    </div>
  );
}
